import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Snackbar,
  Tooltip,
  Dialog,
  Radio,
  RadioGroup,
  FormControlLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from '../services/api';
import { IMAGE_BASE_URLS } from '../utils/imageUrl';
import { getUserData } from '../utils/cookies';
import { decodePlateParams, encodePlateParams, hasURLParams, clearURLParams } from '../utils/urlParams';
import {
  PageName,
  ApiResponse,
  BaseEntity,
  PlateRegion,
  Plate,
  PlateSearchPayload,
  PlateSearchResponse,
  User,
  TaskSummary,
} from '../types';
import { DrawerNavigation, PlateIcon, DRAWER_WIDTH, COLLAPSED_WIDTH, PlateCard, PaginationBar, PlateAlignmentEditor, SendToTaskPanel, ConfirmDialog } from '../components';
import { deletePlates } from '../services/plateService';
import { getTaskBgColor } from '../constants/taskConfig';

// Constants
const IMAGE_BASE_URL = IMAGE_BASE_URLS.plate;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];
const DEBOUNCE_DELAY_MS = 1000;
const THEME_COLOR = '#ff9800';
const THEME_COLOR_HOVER = '#f57c00';

// Country flag emoji based on region code
const getCountryFlag = (code: string): string => {
  if (code.startsWith('us-')) return '🇺🇸';
  if (code.startsWith('ca-')) return '🇨🇦';
  return '';
};

// Get country name from code
const getCountryName = (code: string): string => {
  if (code.startsWith('us-')) return 'USA';
  if (code.startsWith('ca-')) return 'Canada';
  return '';
};

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface PlateSearchProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function PlateSearch({ onLogout, onNavigate }: PlateSearchProps) {
  const theme = useTheme();
  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // User data
  const [user, setUser] = useState<User | null>(null);

  // Dropdown data
  const [datasets, setDatasets] = useState<BaseEntity[]>([]);
  const [flags, setFlags] = useState<BaseEntity[]>([]);
  const [regions, setRegions] = useState<PlateRegion[]>([]);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loadingTasks, setLoadingTasks] = useState<boolean>(true);

  // Search form state
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [tagSearch, setTagSearch] = useState<string>('');
  const [idSearch, setIdSearch] = useState<string>('');
  const [sorting, setSorting] = useState<'normal' | 'reverse' | 'random'>('reverse');
  const [pageSize, setPageSize] = useState<number>(20);

  // Results state
  const [results, setResults] = useState<Plate[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Editing state - track which plate's tag is being edited and its current value
  const [editingTags, setEditingTags] = useState<Record<string, string>>({});
  const tagDebounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  // Selection state for bulk editing
  const [selectedPlates, setSelectedPlates] = useState<string[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // Bulk edit form state
  const [bulkTag, setBulkTag] = useState<string>('');
  const [bulkRegion, setBulkRegion] = useState<string>('');
  const [bulkDataset, setBulkDataset] = useState<string>('');
  const [bulkFlags, setBulkFlags] = useState<string[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Alignment editor state
  const [alignmentEditorOpen, setAlignmentEditorOpen] = useState(false);
  const [editingPlate, setEditingPlate] = useState<Plate | null>(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ref to prevent double fetching in StrictMode
  const hasFetchedInitialData = useRef(false);

  // Ref to track if URL params have been applied
  const hasAppliedURLParams = useRef(false);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
  const totalPages = Math.ceil(totalResults / pageSize);

  // Use Set for O(1) selection lookups
  const selectedPlatesSet = useMemo(() => new Set(selectedPlates), [selectedPlates]);

  // Filter out hidden items
  const visibleResults = useMemo(() =>
    results.filter(p => !hiddenIds.has(p.id)),
    [results, hiddenIds]
  );

  // Sorted regions grouped by country
  const sortedRegions = useMemo(() => {
    return [...regions].sort((a, b) => {
      // Sort by country first (USA before Canada), then by name
      const countryA = getCountryName(a.code);
      const countryB = getCountryName(b.code);
      if (countryA !== countryB) return countryA.localeCompare(countryB);
      return a.label.localeCompare(b.label);
    });
  }, [regions]);

  // Sorted flags
  const sortedFlags = useMemo(() => {
    return [...flags].sort((a, b) => a.label.localeCompare(b.label));
  }, [flags]);

  // Sorted datasets
  const sortedDatasets = useMemo(() => {
    return [...datasets].sort((a, b) => a.label.localeCompare(b.label));
  }, [datasets]);

  // Check if user is admin
  const isAdmin = useMemo(() => user?.roles?.includes('admin') || false, [user]);

  // Fetch initial dropdown data
  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Get user from cookies (stored during login) - no API call needed
        const storedUser = getUserData();
        if (storedUser) {
          setUser({
            id: storedUser.id,
            username: storedUser.username,
            roles: storedUser.roles,
          });
        }

        const [datasetsRes, flagsRes, regionsRes, tasksRes] = await Promise.all([
          api.get<ApiResponse<BaseEntity[]>>('datasets/list/plate'),
          api.get<ApiResponse<BaseEntity[]>>('plate/list/flags'),
          api.get<ApiResponse<PlateRegion[]>>('plate/list/regions'),
          api.get<ApiResponse<TaskSummary[]>>('tasks/list'),
        ]);

        setDatasets(datasetsRes.data.results || []);
        setFlags(flagsRes.data.results || []);
        setRegions(regionsRes.data.results || []);

        // Filter tasks to only show plate type
        const fetchedTasks = (tasksRes.data.results || [])
          .filter((task: TaskSummary) => task.type === 'plate')
          .sort((a: TaskSummary, b: TaskSummary) => a.title.localeCompare(b.title));
        setTasks(fetchedTasks);
        setLoadingTasks(false);
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError('Failed to load form data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Using a ref to store the latest search function to avoid stale closures
  const handleSearchRef = useRef<(resetPage?: boolean) => Promise<void>>(undefined);

  // Apply URL params after initial data is loaded
  useEffect(() => {
    if (loading || hasAppliedURLParams.current || !hasURLParams()) return;
    hasAppliedURLParams.current = true;

    const params = decodePlateParams();

    // Apply params to state
    if (params.dataset) setSelectedDataset(params.dataset);
    if (params.region) setSelectedRegion(params.region);
    if (params.flags && params.flags.length > 0) setSelectedFlags(params.flags);
    if (params.tag) setTagSearch(params.tag);
    if (params.id) setIdSearch(params.id);
    if (params.sort) setSorting(params.sort as 'normal' | 'reverse' | 'random');
    if (params.pageSize) setPageSize(params.pageSize);
    if (params.page) setCurrentPage(params.page);

    // Auto-search with URL params
    setTimeout(() => {
      handleSearchRef.current?.(false);
    }, 0);
  }, [loading]);

  // Handle search
  const handleSearch = useCallback(async (resetPage = true) => {
    setSearching(true);
    setError(null);
    setHiddenIds(new Set());
    if (resetPage) {
      setSelectedPlates([]);
      setBulkTag('');
      setBulkRegion('');
      setBulkDataset('');
      setBulkFlags([]);
      setIdSearch('');
    }

    const page = resetPage ? 1 : currentPage;
    if (resetPage) setCurrentPage(1);

    const payload: PlateSearchPayload = {
      page,
      pageSize,
      sorting,
    };

    if (selectedDataset) payload.dataset = selectedDataset;
    if (selectedRegion) payload.region = selectedRegion;
    if (tagSearch.trim()) payload.tag = tagSearch.trim().toUpperCase();
    if (selectedFlags.length > 0) payload.flags = selectedFlags;
    if (idSearch.trim()) payload.id = idSearch.trim();

    try {
      const response = await api.post<ApiResponse<PlateSearchResponse>>('plate/search', payload);
      const data = response.data.results;
      const plates = data.items || [];
      setResults(plates);
      setTotalResults(data.totalResults || 0);
      setHasSearched(true);

      // Initialize editing tags state
      const initialTags: Record<string, string> = {};
      plates.forEach((plate) => {
        initialTags[plate.id] = plate.tag;
      });
      setEditingTags(initialTags);

      // Update URL with search params
      encodePlateParams({
        dataset: selectedDataset || undefined,
        region: selectedRegion || undefined,
        flags: selectedFlags,
        tag: tagSearch.trim() || undefined,
        id: idSearch.trim() || undefined,
        sort: sorting,
        pageSize: pageSize,
        page: page,
      });
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [currentPage, pageSize, sorting, selectedDataset, selectedRegion, tagSearch, selectedFlags, idSearch]);

  // Handle pagination
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setHiddenIds(new Set());
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setHiddenIds(new Set());
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = useCallback((page: number) => {
    setHiddenIds(new Set());
    setCurrentPage(page);
  }, []);

  // Re-search when page changes
  // Update handleSearchRef with latest handleSearch
  handleSearchRef.current = handleSearch;

  // Track previous page to only re-search when page actually changes
  const prevPageRef = useRef(currentPage);

  useEffect(() => {
    if (hasSearched && currentPage !== prevPageRef.current) {
      handleSearchRef.current?.(false);
    }
    prevPageRef.current = currentPage;
  }, [currentPage, hasSearched]);

  // Handle reset
  const handleReset = () => {
    setSelectedDataset('');
    setSelectedFlags([]);
    setSelectedRegion('');
    setTagSearch('');
    setSorting('normal');
    setPageSize(20);
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
    setHasSearched(false);
    setEditingTags({});
    setSelectedPlates([]);
    setBulkTag('');
    setBulkRegion('');
    setBulkDataset('');
    setBulkFlags([]);
    clearURLParams();
  };

  // Handle tag input change (validates characters)
  const handleTagInputChange = (value: string) => {
    // Allow only uppercase letters, digits, and special pattern characters
    const validChars = /^[A-Z0-9*?@#]*$/;
    const upperValue = value.toUpperCase();
    if (validChars.test(upperValue)) {
      setTagSearch(upperValue);
    }
  };

  // Handle plate tag edit with debounce
  const handlePlateTagChange = useCallback((plateId: string, newTag: string) => {
    // Validate tag - only uppercase letters and digits for actual tags
    const validChars = /^[A-Z0-9]*$/;
    const upperTag = newTag.toUpperCase();
    if (!validChars.test(upperTag)) return;

    // Update local state immediately
    setEditingTags((prev) => ({ ...prev, [plateId]: upperTag }));

    // Clear existing timer
    if (tagDebounceTimers.current[plateId]) {
      clearTimeout(tagDebounceTimers.current[plateId]);
    }

    // Set new timer to send patch after debounce delay
    tagDebounceTimers.current[plateId] = setTimeout(async () => {
      const originalPlate = results.find((p) => p.id === plateId);
      if (!originalPlate || originalPlate.tag === upperTag) return;

      try {
        await api.patch(`plate/patch/${plateId}`, { tag: upperTag });

        // Update results state
        setResults((prev) =>
          prev.map((p) => (p.id === plateId ? { ...p, tag: upperTag } : p))
        );

        setSnackbar({ open: true, message: 'Tag updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update tag:', err);
        setSnackbar({ open: true, message: 'Failed to update tag', severity: 'error' });
        // Revert to original
        setEditingTags((prev) => ({ ...prev, [plateId]: originalPlate.tag }));
      }
    }, DEBOUNCE_DELAY_MS);
  }, [results]);

  // Generic handler for plate field changes (region, dataset, flags)
  const handlePlateFieldChange = useCallback(async (
    plateId: string,
    field: 'region' | 'dataset' | 'flags',
    value: string | string[]
  ) => {
    const originalPlate = results.find((p) => p.id === plateId);
    if (!originalPlate) return;

    // Skip if value hasn't changed
    if (field === 'region' && originalPlate.region.id === value) return;
    if (field === 'dataset' && originalPlate.dataset.id === value) return;

    try {
      // Backend expects objects with 'id' field - onlyIds() extracts them
      const patch: Record<string, unknown> = {};
      if (field === 'region' || field === 'dataset') {
        patch[field] = { id: value as string };
      } else if (field === 'flags') {
        patch[field] = (value as string[]).map((id) => ({ id }));
      }
      await api.patch(`plate/patch/${plateId}`, patch);

      setResults((prev) =>
        prev.map((p) => {
          if (p.id !== plateId) return p;
          const updated = { ...p };

          if (field === 'region') {
            const newRegion = regions.find((r) => r.id === value);
            if (newRegion) updated.region = newRegion;
          } else if (field === 'dataset') {
            const newDataset = datasets.find((d) => d.id === value);
            if (newDataset) updated.dataset = newDataset;
          } else if (field === 'flags' && Array.isArray(value)) {
            updated.flags = value.map((id) => {
              const flag = flags.find((f) => f.id === id);
              return flag || { id: id, label: id };
            });
          }

          return updated;
        })
      );

      const fieldLabels = { region: 'Region', dataset: 'Dataset', flags: 'Flags' };
      setSnackbar({ open: true, message: `${fieldLabels[field]} updated`, severity: 'success' });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      setSnackbar({ open: true, message: `Failed to update ${field}`, severity: 'error' });
    }
  }, [results, regions, datasets, flags]);

  // Cleanup debounce timers on unmount or when results change
  useEffect(() => {
    // Clear all pending debounce timers when results change to prevent stale data
    const timers = tagDebounceTimers.current;
    Object.values(timers).forEach((timer) => clearTimeout(timer));
    tagDebounceTimers.current = {};
  }, [results]);

  // Also cleanup on unmount
  useEffect(() => {
    return () => {
      const timers = tagDebounceTimers.current;
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Selection handlers
  const handleTogglePlateSelection = useCallback((plateId: string) => {
    setSelectedPlates((prev) =>
      prev.includes(plateId)
        ? prev.filter((id) => id !== plateId)
        : [...prev, plateId]
    );
  }, []);

  const handleHideItem = useCallback((id: string) => {
    setHiddenIds(prev => new Set(prev).add(id));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedPlates(visibleResults.map((p) => p.id));
  }, [visibleResults]);

  const handleClearSelection = useCallback(() => {
    setSelectedPlates([]);
  }, []);

  // Bulk edit handlers
  const handleBulkTagChange = (value: string) => {
    const validChars = /^[A-Z0-9]*$/;
    const upperValue = value.toUpperCase();
    if (validChars.test(upperValue)) {
      setBulkTag(upperValue);
    }
  };

  const handleBulkApply = useCallback(async () => {
    if (selectedPlates.length === 0) return;

    // Build patch payload - only include fields that have values
    // Backend expects objects with 'id' field - onlyIds() extracts them
    const patch: { tag?: string; region?: { id: string }; dataset?: { id: string }; flags?: { id: string }[] } = {};
    if (bulkTag.trim()) patch.tag = bulkTag.trim();
    if (bulkRegion) patch.region = { id: bulkRegion };
    if (bulkDataset) patch.dataset = { id: bulkDataset };
    if (bulkFlags.length > 0) patch.flags = bulkFlags.map((id) => ({ id }));

    if (Object.keys(patch).length === 0) {
      setSnackbar({ open: true, message: 'Please set at least one field to update', severity: 'warning' });
      return;
    }

    setBulkUpdating(true);

    // Use Promise.allSettled for parallel requests
    const apiResults = await Promise.allSettled(
      selectedPlates.map((plateId) => api.patch(`plate/patch/${plateId}`, patch))
    );

    // Collect successful plate IDs
    const successfulIds = new Set<string>();
    let failCount = 0;

    apiResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulIds.add(selectedPlates[index]);
      } else {
        console.error(`Failed to update plate ${selectedPlates[index]}:`, result.reason);
        failCount++;
      }
    });

    // Batch update state once for all successful updates
    if (successfulIds.size > 0) {
      setResults((prev) =>
        prev.map((p) => {
          if (!successfulIds.has(p.id)) return p;
          const updated = { ...p };
          if (patch.tag) updated.tag = patch.tag;
          if (patch.region) {
            const regionId = patch.region.id;
            const newRegion = regions.find((r) => r.id === regionId);
            if (newRegion) updated.region = newRegion;
          }
          if (patch.dataset) {
            const datasetId = patch.dataset.id;
            const newDataset = datasets.find((d) => d.id === datasetId);
            if (newDataset) updated.dataset = newDataset;
          }
          if (patch.flags) {
            const patchFlags = patch.flags;
            updated.flags = patchFlags.map((f) => {
              const flag = flags.find((fl) => fl.id === f.id);
              return flag || { id: f.id, label: f.id };
            });
          }
          return updated;
        })
      );

      // Batch update editingTags if tag was changed
      if (patch.tag) {
        setEditingTags((prev) => {
          const updated = { ...prev };
          successfulIds.forEach((id) => {
            updated[id] = patch.tag!;
          });
          return updated;
        });
      }
    }

    setBulkUpdating(false);

    const successCount = successfulIds.size;
    if (failCount === 0) {
      setSnackbar({ open: true, message: `Updated ${successCount} plate(s)`, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: `Updated ${successCount}, failed ${failCount}`, severity: 'warning' });
    }

    // Clear bulk fields after apply
    setBulkTag('');
    setBulkRegion('');
    setBulkDataset('');
    setBulkFlags([]);
  }, [selectedPlates, bulkTag, bulkRegion, bulkDataset, bulkFlags, regions, datasets, flags]);

  // Bulk delete handler - opens confirmation dialog
  const handleBulkDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete handler - executes bulk delete
  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const { succeeded, failed } = await deletePlates(selectedPlates);
      if (succeeded.length > 0) {
        const succeededSet = new Set(succeeded);
        setResults((prev) => prev.filter((p) => !succeededSet.has(p.id)));
        setTotalResults((prev) => prev - succeeded.length);
        setSelectedPlates((prev) => prev.filter((id) => !succeededSet.has(id)));
      }
      if (failed.length > 0) {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length}, failed ${failed.length}`,
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} plate(s)`,
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }, [selectedPlates]);

  // Alignment editor handlers
  const handleImageClick = useCallback((plate: Plate) => {
    setEditingPlate(plate);
    setAlignmentEditorOpen(true);
  }, []);

  const handleAlignmentEditorClose = useCallback(() => {
    setAlignmentEditorOpen(false);
    setEditingPlate(null);
  }, []);

  const handleCornersUpdate = useCallback(async (plateId: string, newCorners: number[][]) => {
    try {
      // Ensure all corner values are integers
      const intCorners = newCorners.map((corner) => corner.map((v) => Math.round(v)));
      await api.patch(`plate/patch/${plateId}`, { corners: intCorners });

      // Update local state
      setResults((prev) =>
        prev.map((p) => (p.id === plateId ? { ...p, corners: intCorners } : p))
      );

      // Update editing plate if it's the same one
      setEditingPlate((prev) =>
        prev && prev.id === plateId ? { ...prev, corners: intCorners } : prev
      );

      setSnackbar({ open: true, message: 'Alignment updated', severity: 'success' });
    } catch (err) {
      console.error('Failed to update corners:', err);
      setSnackbar({ open: true, message: 'Failed to update alignment', severity: 'error' });
      throw err;
    }
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: THEME_COLOR,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <PlateIcon size={28} />
            <Typography variant="h6" noWrap>
              Plate Search
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <DrawerNavigation
        currentPage="searchPlate"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Search Form */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('plate', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PlateIcon size={40} />
              </Box>
              <Box>
                <Typography variant="h5">Search Plates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Search and edit license plate annotations
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Dataset */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Dataset</InputLabel>
                  <Select
                    value={selectedDataset}
                    onChange={(e) => setSelectedDataset(e.target.value)}
                    label="Dataset"
                  >
                    <MenuItem value="">
                      <em>All Datasets</em>
                    </MenuItem>
                    {sortedDatasets.map((dataset) => (
                      <MenuItem key={dataset.id} value={dataset.id}>
                        {dataset.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Region */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    label="Region"
                    renderValue={(value) => {
                      if (!value) return <em>All Regions</em>;
                      const region = regions.find((r) => r.id === value);
                      if (!region) return value;
                      return `${getCountryFlag(region.code)} ${region.label}`;
                    }}
                  >
                    <MenuItem value="">
                      <em>All Regions</em>
                    </MenuItem>
                    {sortedRegions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Typography>{getCountryFlag(region.code)}</Typography>
                        </ListItemIcon>
                        <ListItemText primary={region.label} secondary={region.code} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tag with help icon */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tag Pattern"
                  value={tagSearch}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  placeholder="ABC* or @@123"
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Tag pattern help">
                        <IconButton size="small" onClick={() => setHelpDialogOpen(true)}>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* ID */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID"
                  value={idSearch}
                  onChange={(e) => setIdSearch(e.target.value)}
                  placeholder="MongoDB ObjectId"
                  helperText=" "
                />
              </Grid>

              {/* Flags */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Flags</InputLabel>
                  <Select
                    multiple
                    value={selectedFlags}
                    onChange={(e) => setSelectedFlags(e.target.value as string[])}
                    input={<OutlinedInput label="Flags" />}
                    renderValue={(selected) =>
                      selected
                        .map((id) => flags.find((f) => f.id === id)?.label || id)
                        .join(', ')
                    }
                  >
                    {sortedFlags.map((flag) => (
                      <MenuItem key={flag.id} value={flag.id}>
                        {flag.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Page Size, Sorting and Buttons */}
            <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value as number)}
                    label="Page Size"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Sorting:</Typography>
                  <RadioGroup
                    row
                    value={sorting}
                    onChange={(e) => setSorting(e.target.value as 'normal' | 'reverse' | 'random')}
                  >
                    <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
                    <FormControlLabel value="reverse" control={<Radio size="small" />} label="Reverse" />
                    <FormControlLabel value="random" control={<Radio size="small" />} label="Random" />
                  </RadioGroup>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={handleReset} startIcon={<RefreshIcon />}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleSearch(true)}
                    startIcon={<SearchIcon />}
                    disabled={searching}
                    sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: THEME_COLOR_HOVER } }}
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Results */}
          {hasSearched && (
            <>
              {/* Pagination Header */}
              <Box sx={{ mb: 2 }}>
                <PaginationBar
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalResults={totalResults}
                  disabled={searching}
                  onPrevious={handlePreviousPage}
                  onNext={handleNextPage}
                  onPageChange={handlePageChange}
                />
              </Box>

              {/* Bulk Edit Section */}
              {results.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: getTaskBgColor('plate', theme.palette.mode) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">
                      Bulk Edit ({selectedPlates.length} selected)
                    </Typography>
                    <Button size="small" variant="outlined" onClick={handleSelectAll}>
                      Select All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleClearSelection}
                      disabled={selectedPlates.length === 0}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                  <Grid container spacing={2} alignItems="flex-end">
                    {/* Bulk Tag */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Set Tag"
                        value={bulkTag}
                        onChange={(e) => handleBulkTagChange(e.target.value)}
                        placeholder="e.g. ABC123"
                        inputProps={{ style: { fontFamily: 'monospace', fontWeight: 'bold' } }}
                        disabled={selectedPlates.length === 0}
                      />
                    </Grid>
                    {/* Bulk Region */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedPlates.length === 0}>
                        <InputLabel>Set Region</InputLabel>
                        <Select
                          value={bulkRegion}
                          onChange={(e) => setBulkRegion(e.target.value)}
                          label="Set Region"
                        >
                          <MenuItem value="">
                            <em>-- No change --</em>
                          </MenuItem>
                          {sortedRegions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {getCountryFlag(region.code)} {region.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Bulk Dataset */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedPlates.length === 0}>
                        <InputLabel>Set Dataset</InputLabel>
                        <Select
                          value={bulkDataset}
                          onChange={(e) => setBulkDataset(e.target.value)}
                          label="Set Dataset"
                        >
                          <MenuItem value="">
                            <em>-- No change --</em>
                          </MenuItem>
                          {sortedDatasets.map((dataset) => (
                            <MenuItem key={dataset.id} value={dataset.id}>
                              {dataset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Bulk Flags */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedPlates.length === 0}>
                        <InputLabel>Set Flags</InputLabel>
                        <Select
                          multiple
                          value={bulkFlags}
                          onChange={(e) => setBulkFlags(e.target.value as string[])}
                          input={<OutlinedInput label="Set Flags" />}
                          renderValue={(selected) =>
                            selected
                              .map((id) => flags.find((f) => f.id === id)?.label || id)
                              .join(', ')
                          }
                        >
                          {sortedFlags.map((flag) => (
                            <MenuItem key={flag.id} value={flag.id}>
                              {flag.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Apply Button */}
                    <Grid size={{ xs: 12, md: 'auto' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          onClick={handleBulkApply}
                          disabled={selectedPlates.length === 0 || bulkUpdating}
                          sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: THEME_COLOR_HOVER } }}
                        >
                          {bulkUpdating ? 'Applying...' : 'Apply to Selected'}
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleBulkDeleteClick}
                            disabled={selectedPlates.length === 0 || deleting}
                          >
                            Delete
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Send to Task Panel */}
              {results.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: getTaskBgColor('plate', theme.palette.mode) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Send to task:</Typography>
                    <SendToTaskPanel
                      tasks={tasks}
                      loadingTasks={loadingTasks}
                      selectedIds={selectedPlates}
                      allResultIds={results.map((p) => p.id)}
                      themeColor={THEME_COLOR}
                      onSuccess={(count) => setSnackbar({ open: true, message: `Sent ${count} sample(s) to task`, severity: 'success' })}
                      onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
                    />
                  </Box>
                </Paper>
              )}

              {/* Results Grid */}
              {visibleResults.length > 0 ? (
                <Grid container spacing={2}>
                  {visibleResults.map((plate) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={plate.id}>
                      <PlateCard
                        plate={plate}
                        imageBaseUrl={IMAGE_BASE_URL}
                        isSelected={selectedPlatesSet.has(plate.id)}
                        editingTag={editingTags[plate.id] ?? plate.tag}
                        regions={sortedRegions}
                        datasets={sortedDatasets}
                        flags={sortedFlags}
                        onToggleSelection={handleTogglePlateSelection}
                        onTagChange={handlePlateTagChange}
                        onFieldChange={handlePlateFieldChange}
                        onImageClick={handleImageClick}
                        onHide={handleHideItem}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No plates found matching your criteria.</Typography>
                </Paper>
              )}

              {/* Pagination Footer */}
              {results.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <PaginationBar
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={searching}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                    onPageChange={handlePageChange}
                  />
                </Box>
              )}
            </>
          )}

          {/* Initial State - No search yet */}
          {!hasSearched && !error && (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('plate', theme.palette.mode),
                  display: 'inline-flex',
                  mb: 2,
                }}
              >
                <PlateIcon size={64} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Search for Plate Samples
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the filters above to search for plates by dataset, region, tag pattern, and flags.
                Click the Search button to view results.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Tag Pattern Help Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Tag Pattern Help</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Use the following special characters to search for plate tags:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>*</strong> - Matches any sequence of characters (including empty)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Example: ABC* matches ABC, ABC1, ABC123, ABCDEF
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>?</strong> - Matches any single character
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Example: AB?D matches ABCD, AB1D, ABXD
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>@</strong> - Matches any single letter (A-Z)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Example: @@123 matches AB123, XY123, CD123
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>#</strong> - Matches any single digit (0-9)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Example: ABC### matches ABC123, ABC456, ABC789
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Plate Alignment Editor */}
      {editingPlate && (
        <PlateAlignmentEditor
          open={alignmentEditorOpen}
          plateId={editingPlate.id}
          imageUrl={`${IMAGE_BASE_URL}/${editingPlate.id}`}
          initialCorners={editingPlate.corners}
          onClose={handleAlignmentEditorClose}
          onCornersUpdate={handleCornersUpdate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${selectedPlates.length} Plate(s)`}
        message="Are you sure you want to delete the selected plate(s)? This action cannot be undone."
        loading={deleting}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PlateSearch;
